import { useEffect, useState } from 'react';

/** Port of v0 PartField — same grid cell, input instead of <dd>. */
export function PartField({
  label,
  value,
  onCommit,
  inputId,
  invalid,
  disabled,
  hint,
  placeholder = '—',
  suffix,
}: {
  label: string;
  value: string;
  onCommit: (next: string) => void;
  inputId: string;
  /** L9 #11: incomplete-state — destructive border on a field whose missing
   *  value blocked the save. The silent no-op must be visible, not felt. */
  invalid?: boolean;
  disabled?: boolean;
  /** FieldLabel-style muted note to the right of the label. */
  hint?: string;
  placeholder?: string;
  /** Unit (or similar) rendered next to the input. */
  suffix?: string;
}) {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  return (
    <div>
      {label ? (
        <div className="flex items-baseline justify-between gap-2">
          <label className="block text-muted-foreground" htmlFor={inputId}>
            {label}
          </label>
          {hint ? <span className="text-[11px] text-muted-foreground">{hint}</span> : null}
        </div>
      ) : null}
      <div className="mt-0.5 flex items-center gap-1.5">
        <input
          id={inputId}
          value={draft}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            if (draft !== value) onCommit(draft);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
              e.currentTarget.blur();
            }
          }}
          placeholder={placeholder}
          aria-invalid={invalid || undefined}
          className={[
            'min-h-8 w-full min-w-0 rounded-[9px] border bg-warm-bg px-1.5 py-1 text-[13px] text-foreground outline-none placeholder:text-muted-foreground/60 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-ring',
            invalid
              ? 'border-destructive focus-visible:border-destructive'
              : 'border-input focus-visible:border-coral',
          ].join(' ')}
        />
        {suffix ? (
          <span className="shrink-0 text-[11px] text-muted-foreground">{suffix}</span>
        ) : null}
      </div>
    </div>
  );
}
